var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createThemeScope } from '../theme/themeScope';
import { parseCssPx } from '../theme/themeUtils';
/** Named automotive presets — independent of app `setUiTheme` (Phase 5). */
export var AUTOMOTIVE_THEME_PRESETS = ['classic', 'sport', 'digital'];
/**
 * Automotive HMI palettes.
 * These are **not** driven by `app.setUiTheme()` — change via `props.theme` or
 * `updateAutoWidgetProps(node, { theme: 'sport' })` so clusters keep vehicle-specific look.
 */
export var THEMES = {
    classic: {
        background: '#0a0a0a',
        dialStroke: '#444444',
        needleSpeed: '#ef4444',
        needleTach: '#22c55e',
        text: '#ffffff',
        textMuted: '#9ca3af',
        accent: '#2563eb',
        warning: '#ef4444',
        ok: '#22c55e',
        lampOn: '#fbbf24',
        lampOff: '#333333',
        fontScale: 1,
    },
    sport: {
        background: '#111827',
        dialStroke: '#1f2937',
        needleSpeed: '#f97316',
        needleTach: '#eab308',
        text: '#f9fafb',
        textMuted: '#6b7280',
        accent: '#dc2626',
        warning: '#dc2626',
        ok: '#84cc16',
        lampOn: '#fde047',
        lampOff: '#374151',
        fontScale: 1,
    },
    digital: {
        background: '#020617',
        dialStroke: '#0ea5e9',
        needleSpeed: '#38bdf8',
        needleTach: '#22d3ee',
        text: '#e0f2fe',
        textMuted: '#64748b',
        accent: '#0ea5e9',
        warning: '#f43f5e',
        ok: '#10b981',
        lampOn: '#22d3ee',
        lampOff: '#1e293b',
        fontScale: 1,
    },
};
var fontScaleScope = createThemeScope(function () { return 1; });
/** Per-widget override stack (component `fontSize` prop). */
var fontScaleStack = [];
/** Default HMI preset from ThemePack.automotive (App-scoped via sync). */
var presetScope = createThemeScope(function () { return 'classic'; });
function clampScale(n) {
    return Math.max(0.5, Math.min(2, n));
}
/** Sync automotive label scale from UI `fontSize` (14px → scale 1). */
export function syncAutomotiveFontScale(ui, app, basePx) {
    if (basePx === void 0) { basePx = 14; }
    var scale = 1;
    if (ui && ui.fontSize != null && String(ui.fontSize).trim() !== '') {
        scale = clampScale(parseCssPx(ui.fontSize, basePx) / basePx);
    }
    return fontScaleScope.sync(scale, app !== null && app !== void 0 ? app : undefined);
}
/** Sync default automotive preset from ThemePack.automotive. */
export function syncAutomotiveDefaultPreset(name, app) {
    var next = name === 'classic' || name === 'sport' || name === 'digital' ? name : 'classic';
    return presetScope.sync(next, app !== null && app !== void 0 ? app : undefined);
}
export function getAutomotiveFontScale(app) {
    if (fontScaleStack.length)
        return fontScaleStack[fontScaleStack.length - 1];
    return fontScaleScope.getActive(app !== null && app !== void 0 ? app : undefined);
}
export function getDefaultAutomotivePreset(app) {
    return presetScope.getActive(app !== null && app !== void 0 ? app : undefined);
}
/** Resolve cluster theme name: props.theme → ThemePack.automotive → classic. */
export function autoThemeName(props, app) {
    var t = props.theme;
    if (t === 'classic' || t === 'sport' || t === 'digital')
        return t;
    return getDefaultAutomotivePreset(app);
}
/** Run a widget build with a component-level font scale (beats app scale). */
export function runWithAutomotiveFontScale(scale, fn) {
    fontScaleStack.push(clampScale(scale));
    try {
        return fn();
    }
    finally {
        fontScaleStack.pop();
    }
}
/** Resolve scale from widget props; null → use app scale. */
export function automotiveFontScaleFromProps(props, basePx) {
    if (basePx === void 0) { basePx = 14; }
    if (props.fontSize == null || props.fontSize === '')
        return null;
    return clampScale(parseCssPx(props.fontSize, basePx) / basePx);
}
export function getTheme(name, app) {
    var _a;
    var base = (_a = THEMES[name]) !== null && _a !== void 0 ? _a : THEMES.classic;
    return __assign(__assign({}, base), { fontScale: getAutomotiveFontScale(app) });
}
/** Palette from widget props + optional App pack default. */
export function themeFromProps(props, app) {
    return getTheme(autoThemeName(props, app), app);
}
